import {Form as AntForm} from 'antd';
import React, {useEffect} from 'react';
import {useSelector} from 'react-redux';

import Form from '../../../../components/Form/Form';
import * as requestParamTypes from '../../../../consts/app/requestParamTypes';
import dataCollectorWorkModes from '../../../../consts/dataCollector/dataCollectorWorkModes.ts';
import * as formInputNames from '../../../../consts/form/formInputNames';
import useIsMobileViewport from '../../../../hooks/useIsMobileViewport';
import buildingsService from '../../../../services/buildings/buildingsService';
import gatewaysService from '../../../../services/gateways/gatewaysService';
import log from '../../../../services/logger/log';
import organizationsService from '../../../../services/organizations/organizationsService';
import {setBuildings, setBuildingsOrganizationId} from '../../../../state/ducks/buildings';
import {setOrganizationsNames} from '../../../../state/ducks/organizations';
import {setRequestParams} from '../../../../state/ducks/requestParams';
import {makeSelectGatewaysData} from '../../../../state/selectors/gateways';
import {dispatch} from '../../../../state/store';
import helpers from '../../../../utils/helpers';
import DataCollectorMobileForm from './DataCollectorMobileForm';
import dataCollectorModalService from './dataCollectorModalService';

const DataCollectorModal = ({modalType, onCancel, onOk}) => {
    const isMobile = useIsMobileViewport();
    const [form] = AntForm.useForm();

    const {formFields, title, submitButton, cancelButton, initialValues} = dataCollectorModalService.getFormContent(
        modalType,
        form
    );

    const gatewaysData = useSelector(makeSelectGatewaysData());
    const gatewaysOptions = gatewaysData?.paginatedData?.items || [];

    useEffect(() => {
        document.body.classList.add('data-collector-modal-open');

        return () => document.body.classList.remove('data-collector-modal-open');
    }, []);

    useEffect(() => {
        if (initialValues) {
            form.setFieldsValue(initialValues);

            if (+initialValues[formInputNames.WORK_MODE] === dataCollectorWorkModes.Gateway) {
                gatewaysService.getGatewaysList({page: 1, size: 1000, direction: 1});
            }
            const cityKey = initialValues[formInputNames.CITY]?.key;

            if (cityKey) {
                organizationsService.getOrganizationsNames(cityKey);
            }
        }

        return () => {
            form.resetFields();
            dispatch(setBuildings(null));
            dispatch(setOrganizationsNames(null));
            dispatch(
                setRequestParams({
                    paramsType: requestParamTypes.BUILDINGS,
                    paramsProps: {size: 10},
                })
            );
        };
    }, []);

    useEffect(() => {
        if (gatewaysOptions?.length > 0) {
            const currentGatewayFieldValue = form.getFieldValue(formInputNames.GATEWAY_EUI);

            if (currentGatewayFieldValue?.key && currentGatewayFieldValue.value === currentGatewayFieldValue.key) {
                const matchedGateway = gatewaysOptions.find((g) => g.key === currentGatewayFieldValue.key);

                if (matchedGateway) {
                    form.setFieldsValue({
                        [formInputNames.GATEWAY_EUI]: {
                            key: matchedGateway.key,
                            value: matchedGateway.name,
                        },
                    });
                }
            }
        }
    }, [gatewaysOptions]);

    const onFinish = async () => {
        try {
            const values = await form.validateFields();

            helpers.runFunction(onOk, values);
        } catch (error) {
            log.info('Validate Failed:', error);
        }
    };

    const handleRadioChange = (value, name) => {
        form.setFieldsValue({[name]: value});

        switch (name) {
            case formInputNames.HANDLER: {
                resetFormField([formInputNames.SOURCE]);
                break;
            }
            case formInputNames.WORK_MODE: {
                if (+value !== dataCollectorWorkModes.Gateway) {
                    resetFormField([formInputNames.GATEWAY_EUI]);
                } else {
                    gatewaysService.getGatewaysList({page: 1, size: 1000, direction: 1});
                }
                break;
            }
            default:
                break;
        }
    };
    const setFormField = (name, key, value) => form.setFieldsValue({[name]: {key, value}});

    const resetFormField = (fieldNames) => {
        const fieldsToReset = {};

        fieldNames.forEach((name) => (fieldsToReset[name] = null));
        form.setFieldsValue(fieldsToReset);
    };

    const handleSelect = async (value, name, option) => {
        const currentValue = form.getFieldValue(name);

        if (currentValue?.value === option.value) return;

        setFormField(name, option.key, option.value);

        switch (name) {
            case formInputNames.CITY: {
                resetFormField([formInputNames.ORGANIZATION, formInputNames.BUILDING_ID]);
                await organizationsService.getOrganizationsNames(value);
                break;
            }
            case formInputNames.ORGANIZATION: {
                resetFormField([formInputNames.BUILDING_ID]);
                dispatch(setBuildingsOrganizationId(option?.key));

                dispatch(
                    setRequestParams({
                        paramsType: requestParamTypes.BUILDINGS,
                        paramsProps: {size: 50},
                    })
                );

                await buildingsService.getBuildingsListByOrganizationId(option?.key);
                break;
            }
            default:
                break;
        }
    };

    const handleChange = () => {};

    if (isMobile) {
        return (
            <DataCollectorMobileForm
                form={form}
                modalType={modalType}
                title={title}
                formFields={formFields}
                initialValues={initialValues}
                onFinish={onFinish}
                onCancel={onCancel}
                submitButton={submitButton}
                cancelButton={cancelButton}
                handleRadioChange={handleRadioChange}
                handleSelect={handleSelect}
                handleChange={handleChange}
            />
        );
    }

    return (
        <Form
            handleRadioChange={handleRadioChange}
            handleSelect={handleSelect}
            closeModal={onCancel}
            initialValues={initialValues}
            submitButton={submitButton}
            onFinish={onFinish}
            title={title}
            formFields={formFields.map((field) => ({...field, preserve: true}))}
            cancelButton={cancelButton}
            formInstance={form}
            handleChange={handleChange}
            formClassName='data-collector-form'
            containerClassName='data-collector-form-content'
            buttonsClassName='data-collector-form-actions'
        />
    );
};

export default React.memo(DataCollectorModal);
